import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '@utils/apiBase';
import { fetchWithSession } from '@services/fetchWithSession';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

interface UseDocumentPreviewOptions {
  id: string | undefined;
  /** Depended on by identity, matching the pre-port effect. */
  doc: unknown;
  isActive: boolean;
  language?: 'ja' | 'en';
  theme?: 'light' | 'dark';
}

export interface PreviewError {
  /** Always translated copy: shown to the user, never branched on. */
  message: string;
  /** null when no response was received at all (network failure). */
  status: number | null;
}

/** A non-binary preview outcome, e.g. a deck whose conversion has not finished. */
export interface PreviewState {
  type: string;
  /** Machine-readable failure cause; the client owns the ja/en copy for it. */
  reason?: string;
  slide_count?: number;
}

interface DocumentPreviewState {
  previewUrl: string | null;
  previewContentType: string | null;
  previewState: PreviewState | null;
  /** Slides in a converted deck; 0 for anything else. */
  previewSlideCount: number;
  previewLoading: boolean;
  previewError: PreviewError | null;
  reload: () => void;
}

/** How often an unfinished deck conversion is re-checked. */
const PREVIEW_POLL_INTERVAL_MS = 5000;
/** Stop re-checking after this long — a minute past the server's 240s stale window,
 *  after which it reports the conversion as failed anyway. */
const PREVIEW_POLL_LIMIT_MS = 300000;

/**
 * iframes and <img> cannot send an Authorization header, so the file is fetched
 * as a Blob and handed to the viewer as an object URL.
 */
export function useDocumentPreview({
  id,
  doc,
  isActive,
  language,
  theme,
}: UseDocumentPreviewOptions): DocumentPreviewState {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [previewSlideCount, setPreviewSlideCount] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<PreviewError | null>(null);
  // A ref rather than state: previewLoading in the effect deps made setting it
  // true trigger the cleanup that immediately aborted the request (#220).
  const previewFetchingRef = useRef(false);

  const loadPreview = useCallback(
    async (signal?: AbortSignal, { background = false }: { background?: boolean } = {}) => {
      if (!id) return;

      previewFetchingRef.current = true;

      try {
        // A background re-check must not tear down what is on screen: clearing the
        // state here would disarm the poll that scheduled it.
        if (!background) {
          setPreviewLoading(true);
          setPreviewError(null);
          setPreviewState(null);
        }

        // Yield once before touching the network. StrictMode mounts, cleans up and
        // mounts again in one tick, so without this the phantom mount created a fetch
        // only to abort it in the same tick — the one window some engines report as
        // an unhandled rejection (d938c429, PR #1318). The request never reached the
        // wire either way; what changes is that no promise exists to leak. The
        // phantom's signal is already aborted here.
        await Promise.resolve();
        if (signal?.aborted) return;

        // Read the credential after the yield: a refresh landing in that gap would
        // otherwise send a token fetchWithSession no longer considers current.
        const token = sessionStorage.getItem('access_token');

        // Should an abort still land in the same tick, the rejection handler is
        // attached synchronously; the await below attaches one a microtask too late.
        const query = language && theme ? `?language=${language}&theme=${theme}` : '';
        const request = fetchWithSession(`${API_V1}/documents/${id}/preview${query}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });
        request.catch(() => undefined);

        const response = await request;

        if (signal?.aborted) return;

        // Reported directly rather than thrown: the status alone decides the
        // copy, and the body's `detail` is server prose that must never render.
        if (!response.ok) {
          // The early return skips the catch below, so log here or an HTTP
          // failure leaves no client-side trace at all. Status only — the body
          // is server prose.
          console.error('Document preview request failed:', response.status);
          // A poll rides out a transient failure — an instance restart mid-conversion
          // answers one tick with 5xx, and latching that would strand the deck.
          if (background) return;
          setPreviewError({
            message: t('documents.previewLoadFailedWithStatus', { status: response.status }),
            status: response.status,
          });
          return;
        }

        const contentType = response.headers.get('content-type') || '';

        // A deck that has not finished converting answers with JSON, not a file.
        // Blobbing that would hand the viewer an unreadable object URL.
        if (contentType.split(';')[0].trim() === 'application/json') {
          const state = (await response.json()) as PreviewState;
          if (signal?.aborted) return;
          setPreviewContentType(contentType);
          setPreviewState(state);
          return;
        }

        const blob = await response.blob();

        if (signal?.aborted) return;

        setPreviewContentType(contentType);
        // The file arrived, so any in-progress state is over. Without this the poll
        // stays armed and re-downloads the whole PDF every tick for the full window.
        setPreviewState(null);
        // A converted deck carries its slide count in a header: the body is the PDF.
        setPreviewSlideCount(Number(response.headers.get('x-slide-count')) || 0);
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (err) {
        // Not `instanceof Error`: an abort rejects with DOMException, which does not
        // inherit from Error in every engine, so the guard missed and reported a failure.
        if ((err as { name?: unknown } | null | undefined)?.name === 'AbortError') {
          return;
        }
        if (background) return;
        console.error('Failed to load document preview:', err);
        setPreviewError({
          message: resolveApiErrorMessage(t, err, { fallbackKey: 'documents.previewLoadFailed' }),
          status: null,
        });
      } finally {
        previewFetchingRef.current = false;
        // Reset even when aborted, or the next attempt can never start (#226).
        if (!background) setPreviewLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error copy; explicit presentation options refresh XLSX HTML only.
    [id, language, theme],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Resetting on id change stops a stale document's preview winning the race (#211).
  useEffect(() => {
    setPreviewUrl(null);
    setPreviewContentType(null);
    setPreviewState(null);
    setPreviewSlideCount(0);
    setPreviewLoading(false);
    setPreviewError(null);
    // The previous fetch's finally block may not have run yet; without this the
    // new fetch sees the ref as true and never starts (#226).
    previewFetchingRef.current = false;
  }, [id, language, theme]);

  useEffect(() => {
    const abortController = new AbortController();

    // previewState gates this too: a JSON outcome leaves previewUrl null, and
    // without it the effect would refetch on every render.
    if (
      id && doc && isActive &&
      !previewUrl && !previewState && !previewFetchingRef.current && !previewError
    ) {
      void loadPreview(abortController.signal).catch(() => undefined);
    }

    return () => {
      // Named deliberately: an AbortError here still reading "without reason" came from
      // some other controller.
      abortController.abort(new DOMException('document preview superseded', 'AbortError'));
      // Must be synchronous: the aborted fetch's finally block runs later, and
      // until it does the next effect run would skip fetching (#226).
      previewFetchingRef.current = false;
    };
  }, [id, doc, isActive, previewUrl, previewState, previewError, loadPreview]);

  const reload = useCallback(() => {
    void loadPreview().catch(() => undefined);
  }, [loadPreview]);

  // Conversion runs after upload and nothing pushes the result, so without this poll the
  // spinner never becomes the deck.
  const isConverting =
    previewState?.type === 'preview_converting' || previewState?.type === 'preview_pending';

  useEffect(() => {
    if (!isConverting || !isActive) return undefined;

    const controller = new AbortController();
    const startedAt = Date.now();
    const timer = setInterval(() => {
      // Bounded: a deck that never converts must not be polled for ever.
      if (Date.now() - startedAt >= PREVIEW_POLL_LIMIT_MS) {
        clearInterval(timer);
        return;
      }
      void loadPreview(controller.signal, { background: true }).catch(() => undefined);
    }, PREVIEW_POLL_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [isConverting, isActive, loadPreview]);

  return {
    previewUrl,
    previewContentType,
    previewState,
    previewSlideCount,
    previewLoading,
    previewError,
    reload,
  };
}
